<script>
  import { closeCartDialog } from "#lib/stores.js";
  import LineItem from "#lib/components/utils/LineItem.svelte";
  import CloseIcon from "#lib/components/svg/CloseIcon.svelte";
  import { formatPrice } from "#lib/utils/format.js";
  import { cartStore } from "#lib/stores/cart.js";
  import { browser } from '$app/environment';
  import { onMount } from 'svelte';
  import { captureEvent } from "#lib/utils/posthog.js";
  import { trackCartOpened } from "#lib/utils/engagement.js";
  import { US_COUNTRIES, AU_COUNTRIES } from "#lib/constants.js";
  import { cartUnitCount } from "#lib/utils/cart-quantity.js";
  import { startCheckout } from "#lib/utils/checkout-handoff.js";
  import { dialog } from "#lib/utils/dialog.js";

  let element;
  let errorMessage = "";
  // console.log($userCountryStore);
  // console.log('currencyCode', currencyCode, $userCountryStore, price,subtotal);

  // const CurrenyCountryMap = {
  //    //Us-plug
  //    'US': '$',
  //   'AU': 'AU$',
  //   'NZ': 'NZ$',

  //   //GB-plug
  //   'CY': '£',
  //   'MT': '£',
  //   'IE': '£',
  //   'GB': '£',

  //   //Eu-plug

  //   'AT': 'Austria',
  //   'BE': 'Belgium',
  //   'BG': 'Bulgaria',
  //   'BY': 'Belarus',
  //   'HR': 'Croatia',
  //   'CZ': 'Czech Republic',
  //   'DK': 'Denmark',
  //   'EE': 'Estonia',
  //   'FI': 'Finland',
  //   'EL': 'Greece',
  //   'HU': 'Hungary',
  //   'IE': 'Ireland',
  //   'IT': 'Italy',
  //   'LV': 'Latvia',
  //   'LT': 'Lithuania',
  //   'LU': 'Luxembourg',
  //   'NL': 'Netherlands',
  //   'PL': 'Poland',
  //   'PT': 'Portugal',
  //   'RO': 'Romania',
  //   'SK': 'Slovakia',
  //   'ES': 'Spain',
  //   'SE': 'SEK',
  //   'DE': 'Germany',
  // }


  // Subscribe to cart store
  $: cartItems = $cartStore.items;
  // Units, not lines. A line Shopify zeroed out (no market/shipping coverage
  // for the cart's country) is not an item the shopper has - counting lines
  // rendered "CART (1)" over an empty cart and sent them to a checkout that
  // died with "Cart is empty". See $lib/utils/cart-quantity.js.
  $: cartUnits = cartUnitCount(cartItems);
  $: cartId = $cartStore.id;

  // Track cart drawer opening (edge-detect: fire once per open, not on
  // every cart-store change while the drawer is visible).
  let wasCartOpen = false;
  $: {
    const cartOpen = !$closeCartDialog;
    if (browser && cartOpen && !wasCartOpen) {
      trackCartOpened($cartStore.items?.length ?? 0);
      // A failed page-load init left this drawer empty; try again now rather
      // than showing the visitor an empty cart they know isn't empty.
      if (cartInitFailed) retryCartLoad();
    }
    wasCartOpen = cartOpen;
  }
  $: shopifyTotalAmount = $cartStore.totalAmount;
  $: shopifyTotalCurrency = $cartStore.totalCurrency;
  $: shopifySubtotalAmount = $cartStore.subtotalAmount;
  $: shopifySubtotalCurrency = $cartStore.subtotalCurrency;

  

  // Use Shopify's cart total and currency instead of calculating manually
  $: subtotal = shopifySubtotalAmount || 0;
  $: currencyCode = shopifySubtotalCurrency || 'EUR';

  // Debug logging for cart totals
  // $: {
  //   console.log('🛒 Cart.svelte Debug:', {
  //     shopifyData: {
  //       totalAmount: shopifyTotalAmount,
  //       totalCurrency: shopifyTotalCurrency,
  //       subtotalAmount: shopifySubtotalAmount,
  //       subtotalCurrency: shopifySubtotalCurrency
  //     },
  //     displayData: {
  //       subtotal,
  //       currencyCode,
  //       formattedSubtotal: formatPrice(subtotal, currencyCode)
  //     },
  //     userCountry: $userCountryStore
  //   });
  // }

  // Only fetch cart data when component mounts and we're on a relevant page.
  // Guarded: a transient Shopify failure (e.g. a non-JSON security/rate-limit
  // page for in-app-browser traffic) must not become an unhandled rejection or
  // break the page - the cart re-initializes lazily on the next add-to-cart.
  // Set when init/fetch threw, so opening the drawer can try again. 24 of
  // these in 30 days (20 visitors), mostly a plain "Failed to fetch" - and
  // because init ran only in onMount, the drawer stayed empty until the
  // visitor reloaded the page.
  let cartInitFailed = false;
  let cartRetryInFlight = false;

  async function loadCart() {
    try {
      await cartStore.init();
      await cartStore.fetch();
      cartInitFailed = false;
      return true;
    } catch (error) {
      console.error('Cart: init/fetch failed', error);
      cartInitFailed = true;
      // cart_init_failed is NOT raised here any more. This catch wraps init()
      // and fetch() together, so the event it used to send could not say which
      // call had failed - and one of them is same-origin while the other was
      // the Shopify read. The store raises it instead, with a `stage`
      // (hub ledger 2026-09-23-069). Raising it here as well would double-count
      // every failure.
      return false;
    }
  }

  onMount(loadCart);

  /** Retry on open: the drawer is the one moment the visitor is looking at it. */
  async function retryCartLoad() {
    if (cartRetryInFlight) return;
    cartRetryInFlight = true;
    const ok = await loadCart();
    cartRetryInFlight = false;
    captureEvent('cart_init_retried', { succeeded: ok, trigger: 'drawer_open' });
  }
  


  function handleClick(e) {
    if (e.clientX < element?.getBoundingClientRect()?.left) {
      closeCartDialog.set(true);
    }
  }

  async function handleCheckout() {
    // A retry starts clean: the drawer stays open on failure, so a stale
    // message from the previous attempt would sit under the button.
    errorMessage = "";

    // Refetch first: the drawer is the one place the persistent cart may be
    // stale. The store's own `error` is forwarded so startCheckout can tell an
    // unreadable cart apart from an empty one.
    await cartStore.fetch();
    const cartData = $cartStore;

    const result = await startCheckout({
      source: 'cart_drawer',
      cart: {
        items: cartData.items,
        checkoutUrl: cartData.checkoutUrl,
        error: cartData.error,
      },
    });

    if (!result.ok) errorMessage = result.error;
  }
</script>

<svelte:window on:click|capture={handleClick} />

{#if !$closeCartDialog}
  <!-- A real dialog: it covers the page and takes clicks, so Escape must
       close it, Tab must stay inside it, and focus must go back to the cart
       button afterwards. See $lib/utils/dialog.js. -->
  <div
    class="cart-parent-container"
    bind:this={element}
    role="dialog"
    aria-modal="true"
    aria-label="Shopping cart"
    tabindex="-1"
    use:dialog={{ onClose: () => closeCartDialog.set(true) }}
  >
    <div class="cart-header">
      <button
        class="close-button cl-btn"
        aria-label="Close cart"
        on:click={() => closeCartDialog.set(true)}
      >
        <CloseIcon />
      </button>

      {#if cartUnits > 0}
        <h1 class="flex-center">
          Cart <span class="item-count">{cartUnits}</span>
        </h1>
      {/if}
    </div>

    <div class="cart-content">
      {#if cartUnits === 0}
        <p class="center">Cart is empty</p>
      {:else}
        <ul class="cl-ul">
          {#each cartItems as item (item.node?.id || item.id)}
            <li>
              <LineItem {item} />
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    {#if cartUnits > 0}
      <div class="cart-footer">
        <div class="checkout">
          <div class="subtotal">
            <span>Subtotal</span>
            <span>
              {formatPrice(subtotal, currencyCode)}
            </span>
          </div>
        </div>
        
        <button 
          class="checkout-button" 
          on:click={handleCheckout}
          disabled={cartUnits === 0}
        >
          Proceed to checkout
        </button>
        
        {#if errorMessage}
          <!-- role="alert" so a checkout failure is announced instead of
               appearing silently below the button. The PDP's equivalent
               already had one. -->
          <p class="error-message" role="alert">{errorMessage}</p>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  h1, span, p {
    color: black;
  }

  .cart-parent-container {
    box-sizing: border-box;
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    width: 400px;
    background-color: #D0D8D6;
    z-index: 999;
    color: black;
    display: flex;
    flex-direction: column;

    @media (max-width: 440px) {
      width: 100%;
    }
  }

 

  .cart-header {
    padding: 1rem;
    position: relative;
    flex-shrink: 0;
  }

  .cart-content {
    flex-grow: 1;
    overflow-y: auto;
    padding: 0 1rem;
    
    /* Hide scrollbar by default, show only when hovering */
    scrollbar-width: thin;
    scrollbar-color: var(--accent-color) transparent;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: var(--accent-color);
      border-radius: 3px;
    }
  }

  .cart-footer {
    padding: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    background-color: #D0D8D6;
  }

  .checkout-button {
    width: 100%;
    margin-top: 1rem;
    height: 3rem;
    color: white;
    background-color: black;
    padding: 1rem;
    font-family: "Figtree";
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
  
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
      opacity: 0.9;
      background-color:white;
      color: black;
    }

    &:disabled {
      background-color: #484848;
      color: #A1A1A1;
      cursor: not-allowed;
    }
  }

  .error-message {
    color: #ff4444;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    text-align: center;
  }

  .flex-center {
    gap: 1rem;
    margin: 0;
    display: flex;
    align-items: center;
  }

  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    z-index: 1000;
  }

  .checkout {
    color: black;

    .subtotal {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 1.1rem;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: black;
    }
  }

  .item-count {
    color: var(--primary-color);
    background-color: var(--secondary-color);
    font-size: 1rem;
    border-radius: 200rem;
    padding: 0.1rem 0.5rem;
    display: inline-block;
  }

  .cl-ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .center {
    text-align: center;
    margin: 2rem 0;
  }
</style>
